import { useCallback } from "react";
import { useMst } from "../../../stores/main";

function cloneAnimationDraft<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

type AnimationEditCallback<T> = (workingAnimation: unknown) => T;
type AnimationJsonEditOptions = {
  manageUndo?: boolean;
  playerUpdate?: {
    forceRecreate?: boolean;
    immediate?: boolean;
    startFrame?: number;
  };
};

export function useAnimationJsonEdit() {
  const {
    playerStore: { jsonFile, jsonFileName, replaceDraftJson, runPlayer },
    undoStore,
  } = useMst();

  const applyAnimationJsonEdit = useCallback(
    <T>(
      edit: AnimationEditCallback<T>,
      options?: AnimationJsonEditOptions
    ): T | false => {
      if (!jsonFile) {
        return false;
      }

      const manageUndo = options?.manageUndo !== false;
      const workingAnimation = cloneAnimationDraft(jsonFile);

      if (manageUndo) {
        undoStore.onGestureStart();
      }

      try {
        const result = edit(workingAnimation);
        if (result === false) {
          return false;
        }

        if (options?.playerUpdate) {
          replaceDraftJson(workingAnimation, jsonFileName, {
            suppressPlayerAutorun: true,
          });
          runPlayer({
            ...options.playerUpdate,
            animationChange: workingAnimation,
          });
        } else {
          replaceDraftJson(workingAnimation, jsonFileName);
        }
        return result;
      } finally {
        if (manageUndo) {
          undoStore.onGestureEnd();
        }
      }
    },
    [jsonFile, jsonFileName, replaceDraftJson, runPlayer, undoStore]
  );

  return {
    applyAnimationJsonEdit,
    animationJsonDraft: jsonFile,
  };
}
